import { ComponentProps, FC } from "react";
import * as _ from "lodash";

import {
  FullscreenButton,
  Row,
  Col,
  Icon,
  CloseOutlined,
  Analysis,
  Popover,
  Grid,
  StarOutlined,
  StarFilled,
  Tooltip,
  Button,
  Charts
} from "@ctra/components";

import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { ChartEntity, Enterprise, FavouritesFilterConfig } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";
import { useLegendValues } from "@ctra/charts";

import { ChartProps, ChartViewFilter, useChartAPI, useChartFilters } from "@chart";
import { useFavourites } from "@analytics";

import { AnomalyToggle } from "../AnomalyToggle";
import { GAActions } from "../../analytics";
import { testIds } from "../testing";
import styles from "./ChartExtra.module.less";

export type ChartExtraProps = Omit<ChartProps, "filters">;

/**
 * Extra tools for chart components: full screen/chart comparison
 * @param {(() => void) | undefined} handleCompare
 * @param {(() => void) | undefined} handleClose
 * @param {ChartViewOptions | undefined} viewOptions
 * @param {Options | (Omit<Options, "data" | "yAxis" | "color" | "annotations"> & {readonly data: Array<Record<string, any>[]>, readonly xField: string, readonly yField: string[], readonly yAxis?: Options["yAxis"][] | Record<string, Options["yAxis"]>, readonly geometryOptions?: GeometryOption[], readonly annotations?: Options["annotations"][] | Record<string, Options["annotations"]>}) | GaugeOptions | TinyPlotOptions | BulletOptions | MixOptions | TreemapOptions | undefined} config
 * @return {JSX.Element}
 * @constructor
 */
export const ChartExtra: FC<ChartExtraProps> = ({
  handleCompare,
  handleClose,
  viewOptions = {},
  variant,
  config
}) => {
  const { t } = useTranslation();
  const { md } = Grid.useBreakpoint();
  const { trackEvent, label } = useGoogleAnalytics<{ label: string }>();
  const { legendValues } = useLegendValues();

  const {
    compare,
    favourites: { add, remove }
  } = Content.chart;

  const {
    chart,
    meta,
    api,
    dataDescriptor: { id: dataDescriptorID }
  } = useChartAPI();

  const {
    metadata: {
      supportedfarmIDs: farmIDs,
      flags: { isBackendAggregated }
    },
    isoDuration,
    timePeriod: { startDate, endDate },
    filters: { dimGroupKey, herdGroupID }
  } = useChartFilters();

  const { setChart } = api;
  const { supportedViews } = meta;
  const { view } = chart as ChartEntity;
  const { favourites, addToFavourites, removeFromFavourites } = useFavourites();
  const isFavourite = _.includes(_.keys(favourites), dataDescriptorID);
  const Star = isFavourite ? StarFilled : StarOutlined;

  /**
   * Handle chart view changes
   * @param view
   */
  const onViewChange: ComponentProps<typeof ChartViewFilter>["onViewChange"] = (view) => {
    setChart(supportedViews[view]);
  };

  /**
   * either an ISO duration or custom date range (iso duration becomes null on custom)
   */
  const dateRange = isoDuration ? [isoDuration] : [startDate, endDate];

  /**
   * If not backendAggregated then we only care about display filters (no chart reload)
   */
  const displayFilters = [
    ...(dimGroupKey && !isBackendAggregated
      ? [{ type: FavouritesFilterConfig.dimGroup, values: dimGroupKey }]
      : []),
    ...(herdGroupID && !isBackendAggregated
      ? [{ type: FavouritesFilterConfig.herdGroup, values: herdGroupID }]
      : []),
    ...(legendValues.length ? [{ type: FavouritesFilterConfig.legend, values: legendValues }] : [])
  ];

  const dataFilters = [
    ...(dimGroupKey ? [{ type: FavouritesFilterConfig.dimGroup, values: dimGroupKey }] : []),
    ...(herdGroupID ? [{ type: FavouritesFilterConfig.herdGroup, values: herdGroupID }] : [])
  ];

  return (
    <Row
      justify="space-around"
      gutter={[0, 0]}
      align="middle"
      className={styles.Wrapper}
      onClick={(evt) => {
        evt.stopPropagation();
      }}
    >
      {viewOptions.showFavouriteIcon && (
        <Tooltip title={t(isFavourite ? remove : add)}>
          <Star
            className={styles.Favourites}
            onClick={() => {
              /* istanbul ignore next */
              trackEvent(GAActions[isFavourite ? "removeFromFavourites" : "addToFavourites"]);

              if (isFavourite) {
                removeFromFavourites({
                  dataDescriptorID,
                  view,
                  dateRange,
                  displayFilters,
                  ...(isBackendAggregated ? { dataFilters } : void 0)
                });
              } else {
                addToFavourites({
                  dataDescriptorID,
                  view,
                  dateRange,
                  displayFilters,
                  ...(isBackendAggregated ? { dataFilters } : void 0)
                });
              }
            }}
          />
        </Tooltip>
      )}
      {viewOptions.showAnnotations && _.isEqual(variant, Charts.ChartVariant.V3) && (
        <Col pull={2}>
          <AnomalyToggle />
        </Col>
      )}
      {viewOptions.showSupportedViews && _.keys(supportedViews).length > 1 && (
        <Col className={styles.ViewFilter}>
          <ChartViewFilter views={_.keys(supportedViews)} view={view} onViewChange={onViewChange} />
        </Col>
      )}
      {handleCompare && farmIDs && (
        <Popover content={t<string>(compare)} placement="topRight" overlayClassName={styles.Popover}>
          <Button
            data-testid={testIds.chartExtra.compareCharts}
            onClick={() => {
              /* istanbul ignore next */
              trackEvent(GAActions.goToCompareCharts, { label });
              handleCompare();
            }}
            icon={<Icon component={Analysis} />}
          />
        </Popover>
      )}
      {/* exit full screen button */}
      {viewOptions.zoomed && md && (
        <FullscreenButton
          exit={true}
          data-testid={testIds.chartExtra.zoom}
          onClick={() => {
            trackEvent(GAActions.collapseChart, { label });

            Enterprise.history.push({
              state: {}
            });
          }}
        />
      )}
      {_.isFunction(handleClose) && <CloseOutlined className={styles.Close} onClick={handleClose} />}
    </Row>
  );
};
